import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

export class QuickAddComponent extends Component {
  #abortController = null;
  #cachedContent = new Map();

  get productPageUrl() {
    const productCard = this.closest('product-card');
    const productLink = productCard?.getProductCardLink();
    if (!productLink?.href) return '';

    const url = new URL(productLink.href);
    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) url.searchParams.set('variant', selectedVariantId);

    return url.toString();
  }

  #getSelectedVariantId() {
    const productCard = this.closest('product-card');
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();
    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
  }

  handleClick = async (event) => {
    event.preventDefault();
    const currentUrl = this.productPageUrl;
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          productGrid = gridElement.cloneNode(true);
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      const freshContent = productGrid.cloneNode(true);
      await this.updateQuickAddModal(freshContent);
    }

    this.#openQuickAddModal();
  };

  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);
    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => {
      this.toggleAttribute('stay-visible', false);
    }, { once: true });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);
    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, { signal: this.#abortController.signal });
      if (!response.ok) throw new Error(`Failed to fetch product page: ${response.status}`);
      const text = await response.text();
      return new DOMParser().parseFromString(text, 'text/html');
    } catch (error) {
      if (error.name === 'AbortError') return null;
      throw error;
    } finally {
      this.#abortController = null;
    }
  }

  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      if (!productDetails) return;

      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');

      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';
      productTitle.href = this.productPageUrl;

      if (!productFormComponent || !variantPicker || !productPrice) return;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');
      productHeader.appendChild(productTitle);
      productHeader.appendChild(productPrice);

      productGrid.appendChild(productHeader);
      productGrid.appendChild(variantPicker);
      productGrid.appendChild(productFormComponent);
      productDetails.remove();
    }

    morph(modalContent, productGrid);
    this.#syncVariantSelection(modalContent);
  }

  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);

    this.#initDragHandle();
    this.#initSwipeToClose();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (event) => {
    const anchorElement = event.detail.data.html?.querySelector('.view-product-title a');
    const viewMoreDetailsLink = this.querySelector('.view-product-title a');
    const mobileProductTitle = this.querySelector('.product-header a');

    if (!anchorElement) return;
    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => { grid.style.width = ''; });
      }
    });
  };

  #initDragHandle() {
    const handle = this.querySelector(".quick-add-modal__handle");
    const modal = this.querySelector(".quick-add-modal");
    if (!handle || !modal) return;

    let startY = 0, currentY = 0, isDragging = false;
    const self = this;

    const startDrag = (e) => {
      isDragging = true;
      startY = e.touches ? e.touches[0].clientY : e.clientY;
      modal.style.transition = "none";

      document.addEventListener("mousemove", drag);
      document.addEventListener("touchmove", drag, { passive: false });
      document.addEventListener("mouseup", endDrag);
      document.addEventListener("touchend", endDrag);
    };

    const drag = (e) => {
      if (!isDragging) return;
      currentY = e.touches ? e.touches[0].clientY : e.clientY;
      const delta = currentY - startY;
      if (delta > 0) modal.style.transform = `translateY(${delta}px)`;
    };

    const endDrag = () => {
      if (!isDragging) return;
      isDragging = false;
      const delta = currentY - startY;
      if (delta > 100) self.closeDialog();
      else {
        modal.style.transition = "transform 0.3s ease";
        modal.style.transform = "translateY(0)";
      }

      document.removeEventListener("mousemove", drag);
      document.removeEventListener("touchmove", drag);
      document.removeEventListener("mouseup", endDrag);
      document.removeEventListener("touchend", endDrag);
    };

    handle.addEventListener("mousedown", startDrag);
    handle.addEventListener("touchstart", startDrag);
  }

  #initSwipeToClose() {
    if (!isMobileBreakpoint()) return;
    const modal = this.querySelector(".quick-add-modal");
    if (!modal) return;

    let startY = 0, currentY = 0;

    const onTouchStart = (e) => { startY = e.touches[0].clientY; modal.style.transition = "none"; };
    const onTouchMove = (e) => { currentY = e.touches[0].clientY; const delta = currentY - startY; if (delta > 0) modal.style.transform = `translateY(${delta}px)`; };
    const onTouchEnd = () => { const delta = currentY - startY; if (delta > 100) this.closeDialog(); else { modal.style.transition = "transform 0.3s ease"; modal.style.transform = "translateY(0)"; } };

    modal.addEventListener("touchstart", onTouchStart, { passive: true });
    modal.addEventListener("touchmove", onTouchMove, { passive: false });
    modal.addEventListener("touchend", onTouchEnd);
  }
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}

document.addEventListener('DOMContentLoaded', () => {
  const dialog = document.querySelector('#quick-add-dialog dialog');
  if (!dialog) return;

  let scrollTop = 0;

  const lockBodyScroll = () => {
    scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'relative';
  };

  const unlockBodyScroll = () => {
    document.body.style.overflow = '';
    document.body.style.position = '';
  };

  const observer = new MutationObserver(() => {
    if (dialog.hasAttribute('open')) lockBodyScroll();
    else unlockBodyScroll();
  });

  observer.observe(dialog, { attributes: true });

  const modalContent = dialog.querySelector('.quick-add-modal__content');
  if (modalContent) {
    let startY = 0;
    modalContent.addEventListener('touchstart', e => { startY = e.touches[0].pageY; }, { passive: false });
    modalContent.addEventListener('touchmove', e => {
      const currentY = e.touches[0].pageY;
      const scrollTop = modalContent.scrollTop;
      const scrollHeight = modalContent.scrollHeight;
      const offsetHeight = modalContent.offsetHeight;
      const atTop = scrollTop === 0;
      const atBottom = scrollTop + offsetHeight >= scrollHeight - 1;

      if ((atTop && currentY > startY) || (atBottom && currentY < startY)) e.preventDefault();
    }, { passive: false });
  }
});
/* ZICABELLA — CLOSE QUICK ADD POPUP ONLY ON BUY NOW */
document.addEventListener('click', function(e) {
  const buyNowBtn = e.target.closest('.shopify-payment-button__button,' +
      '.sr-headless-checkout,' +
      '.sr-headless-checkout *,' +
      '[data-accelerated-checkout],' +
      '[name="checkout"]');
  if (!buyNowBtn) return;

  setTimeout(() => {
    const dialog = document.getElementById('quick-add-dialog');

    if (dialog && typeof dialog.closeDialog === 'function') {

      // Correct way to close the popup
      dialog.closeDialog();
      dialog.removeAttribute('open');

      // Force hide modal panel
      const modal = dialog.querySelector('.quick-add-modal');
      if (modal) {
        modal.style.transform = 'translateY(100vh)';
        modal.style.opacity = '0';
        modal.style.transition = '0.2s ease';
      }

      // Unlock scroll
      document.body.style.overflow = 'auto';
      document.body.classList.remove('overflow-hidden', 'modal-open', 'scroll-lock');
    }

  }, 80);

}, true);
